import { queryOptions, useQueries } from "@tanstack/react-query";

import type { ICategoryComparison } from "app/pages/industry-analysis/categories-context/CategoriesContext";
import { getQueryParams } from "app/pages/industry-analysis/common/getQueryParams";
import { DefaultFetchService } from "app/services/fetchService";

import { ENDPOINT, ROWS_TO_DISPLAY } from "../consts";
import type { GeographyCategoryTableResponse } from "../types";

import type { CountryData, TopCountriesComparisonRow } from "./types";

const combineTopCountriesData = (
    primaryCategoryData: CountryData[],
    comparedCategoryData: CountryData[],
): TopCountriesComparisonRow[] => {
    if (!primaryCategoryData || !comparedCategoryData) {
        return null;
    }

    return Array.from({ length: ROWS_TO_DISPLAY }).map((_, index) => ({
        countryRank: index + 1,
        primaryCategory: primaryCategoryData[index] || null,
        comparedCategory: comparedCategoryData[index] || null,
    }));
};

const selectTableData = ({ Data }: GeographyCategoryTableResponse, categoryColor: string): CountryData[] => {
    return Data.slice(0, ROWS_TO_DISPLAY).map((dataItem) => ({
        countryCode: dataItem.Country,
        marketShare: dataItem.TrafficShare,
        change: dataItem.GrowthPercentage,
        color: categoryColor,
    }));
};

const fetchService = DefaultFetchService.getInstance();

const createQuery = (comparisonCategory: ICategoryComparison) => {
    const { category, color } = comparisonCategory;
    const baseQueryParams = getQueryParams();
    const queryParams = {
        ...baseQueryParams,
        keys: category.forApi,
        categories: category.forApi,
        categoryHash: category.categoryHash,
    };

    return queryOptions({
        queryKey: [ENDPOINT, queryParams],
        queryFn: () => fetchService.get<GeographyCategoryTableResponse>(ENDPOINT, queryParams),
        select: (data: GeographyCategoryTableResponse) => selectTableData(data, color),
    });
};

export const useTopCountriesComparisonData = (categories: ICategoryComparison[]) => {
    const [primaryCategory, comparedCategory] = categories;

    return useQueries({
        queries: [createQuery(primaryCategory), createQuery(comparedCategory)],
        combine: (results) => {
            const [primaryQuery, comparedQuery] = results;

            return {
                data: combineTopCountriesData(primaryQuery.data, comparedQuery.data),
                isLoading: primaryQuery.isLoading || comparedQuery.isLoading,
                isError: primaryQuery.isError || comparedQuery.isError,
            };
        },
    });
};
